import { Show, createSignal, For, createEffect, Suspense } from "solid-js";
import { createAsync, useLocation, A } from "@solidjs/router";
import { getMenu } from "~/_server";

import { useRIASApp } from '~/_context/UserProvider';

import { Close } from '~/components';
import { useProfile } from "~/_context/ProfileProvider";

import { LogOut } from "lucide-solid";

// Drop the client-side copies of the tokens, then let the server clear the
// httpOnly cookies and bounce through Cognito's hosted logout.
const signOut = () => {
	try {
		localStorage.removeItem( "accessToken" );
		localStorage.removeItem( "idToken" );
		sessionStorage.removeItem( "code_verifier" );
	} catch { }

	window.location.href = "/api/auth/logout";
};

const SignOutButton = () => (
	<button
		type="button"
		onClick={signOut}
		title="Sign out"
		class="relative rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden cursor-pointer">
		<span class="absolute -inset-1.5"></span>
		<span class="sr-only">Sign out</span>
		<LogOut class="size-6" stroke-width={1.5} aria-hidden="true" />
	</button>
);


export const Nav = () => {
	const { isImpersonating, setImpersonation } = useRIASApp();
	const { profile } = useProfile();

	const [isMenuOpen, setIsMenuOpen] = createSignal( false );

//	createEffect( () => console.log( 'profile', profile.latest ) );

	return (
		<header class="bg-primary pb-32">
			<Show when={isImpersonating()}>
				<div class="bg-red-800 text-white py-2 mb-2 text-center" >
					<div class="main-container flex flex-nowrap items-center justify-center">Impersonating <span class="mx-3 font-bold uppercase">{profile.latest?.name}</span><span><Close onClick={() => { setImpersonation( 0 ) }} width="30px" classList={{ "mr-4": true, "cursor-pointer": true }} /></span></div>
				</div>
			</Show>

			<nav class="main-container">
				<div class="border-b border-gray-200">
					<div class="flex h-16 items-center justify-between px-4 sm:px-0">
						<div class="flex items-center">
							<div class="shrink-0">
								<img src="/images/rias-regs.svg" alt="RIAS-regs" class="w-[60px]" />
							</div>
							<div class="hidden md:block">
								<div class="ml-10 flex items-baseline space-x-4">
									<DashboardLinks />
								</div>
							</div>
						</div>
						<div class="hidden md:block">
							<div class="ml-4 flex items-center gap-4 md:ml-6">
								<span class="mr-1 text-gray-400">{profile.latest?.firstName} {profile.latest?.lastName}</span>
								<SignOutButton />
								<button
									type="button"
									class="relative rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden">
									<span class="absolute -inset-1.5"></span>
									<span class="sr-only">View notifications</span>
									<svg
										class="size-6"
										fill="none"
										viewBox="0 0 24 24"
										stroke-width="1.5"
										stroke="currentColor"
										aria-hidden="true"
										data-slot="icon">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
									</svg>
								</button>
								<Show when={false}>
									{/* Profile dropdown */}
									<div class="relative ml-3">
										<div>
											<button
												type="button"
												class="relative flex max-w-xs items-center rounded-full bg-gray-800 text-sm focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden"
												id="user-menu-button"
												aria-expanded="false"
												aria-haspopup="true">
												<span class="absolute -inset-1.5"></span>
												<span class="sr-only">Open user menu</span>
												<img
													class="size-8 rounded-full"
													src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
													alt="" />
											</button>
										</div>

										{/*
                    Dropdown menu, show/hide based on menu state.

                    Entering: "transition ease-out duration-100"
                      From: "transform opacity-0 scale-95"
                      To: "transform opacity-100 scale-100"
                    Leaving: "transition ease-in duration-75"
                      From: "transform opacity-100 scale-100"
                      To: "transform opacity-0 scale-95"
                */}
										<Show when={isMenuOpen()}>
											<div
												class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden"
												role="menu"
												aria-orientation="vertical"
												aria-labelledby="user-menu-button"
												tabindex="-1">
												{/*<!-- Active: "bg-gray-100 outline-hidden", Not Active: "" -->*/}
												<ProfileLinks />
											</div>
										</Show>
									</div>
								</Show>

							</div>
						</div>
						<div class="-mr-2 flex md:hidden">
							{/*<!-- Mobile menu button -->*/}
							<button
								type="button"
								class="relative inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden"
								aria-controls="mobile-menu"
								aria-expanded="false"
								onClick={() => setIsMenuOpen( s => !s )}>
								<span class="absolute -inset-0.5"></span>
								<span class="sr-only">Open main menu</span>
								<svg
									class="block size-6"
									fill="none"
									viewBox="0 0 24 24"
									stroke-width="1.5"
									stroke="currentColor"
									aria-hidden="true"
									data-slot="icon">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d={!isMenuOpen() ? "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" : "M6 18 18 6M6 6l12 12"} />
								</svg>
							</button>
						</div>
					</div>
				</div>
				<Show when={isMenuOpen()}>
					<MobileMenu />
				</Show>
			</nav>

			<div class="main-container mx-auto lg:text-3xl text-white my-6"><h1 id="main-title"></h1></div>
		</header>
	);
}


const MobileMenu = () => {
	const { profile } = useProfile();

	return (
		<div class="border-b border-gray-700 md:hidden" id="mobile-menu">
			<div class="space-y-1 px-2 py-3 sm:px-3">
				<DashboardLinks />
			</div>
			<div class="border-t border-gray-700 pt-4 pb-3">
				<div class="flex items-center px-5">
					<div class="shrink-0">
						<img
							class="size-10 rounded-full"
							src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
							alt="" />
					</div>
					<div class="ml-3">
						<div class="text-base/5 font-medium text-white">{profile.latest?.firstName} {profile.latest?.lastName}</div>
						<div class="text-sm font-medium text-gray-400">
							{profile.latest?.email}
						</div>
					</div>
					<div class="ml-auto shrink-0">
						<SignOutButton />
					</div>
					<button
						type="button"
						class="relative ml-2 shrink-0 rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden">
						<span class="absolute -inset-1.5"></span>
						<span class="sr-only">View notifications</span>
						<svg
							class="size-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke-width="1.5"
							stroke="currentColor"
							aria-hidden="true"
							data-slot="icon">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
						</svg>
					</button>
				</div>
				<div class="mt-3 space-y-1 px-2">
					<ProfileLinks />
				</div>
			</div>
		</div> );
}


const title = "RIAS-regs Dashboard";

const DashboardLinks = () => {
	const location = useLocation();
	const {	profile } = useProfile();
	
	const linkData = createAsync( () => getMenu(), { deferStream: true } );

	createEffect( () => {
		if ( linkData()?.links ) {
			const path = location.pathname;
			const link = linkData()?.links.find( l => l.href === path );

			const fullTitle = `${title}${link ? ` - ${link.caption}` : ''}`;

			document.title = fullTitle;

			if ( fullTitle.length > title.length ) {
				try {
					document.getElementById( 'main-title' )!.innerText = fullTitle;
				} catch { };
			}
		}
	} );

	return (
		<Suspense fallback={null}>
			<Show when={linkData()?.links}>
				<For each={linkData()?.links} >
					{
						link => ( typeof link.inMenu == 'undefined' || link.inMenu ) && ( !link.exCPD || profile.latest?.role !== 'cpd' ) && (
							<A
								href={link.href}
								class="block rounded-md px-3 py-2 text-base font-medium hover:bg-gray-700 hover:text-white"
								activeClass="bg-gray-900 text-white"
								inactiveClass="text-gray-300"
								end={true}>
								{link.caption}</A>
						)
					}
				</For>
			</Show>
		</Suspense>
	)
}

const ProfileLinks = () => {
	return (
		<>
			<A href="#"
				class="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
			>Your Profile</A>
			<A href="#"
				class="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
			>Settings</A>
			<button type="button" onClick={signOut}
				class="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white cursor-pointer"
			>Sign out</button>
		</>
	);
}